import React, { useEffect, useMemo, useRef, useState } from "react";
import "./EditorLeftMenuLayersControls.css";
import {
  isLayerVisible,
  sortLayersTopFirst,
  useEditorStore,
} from "../../../../state/editorStore";
import {
  EyeIcon,
  DragHandleIcon,
  TrashIcon,
  LockIcon,
  FrontIcon,
  ForwardIcon,
  BackwardIcon,
  BackIcon,
} from "../../../../icons";

const LIST_GAP = 4;
const PANEL_PAD_X = 20;

/** Background kinds: none (transparent), solid color, image, or AI. */
function getBackgroundDisplay(backgroundPreview) {
  const type = backgroundPreview?.type;
  if (type === "image" && backgroundPreview?.imageUrl) {
    return {
      kind: "image",
      label: "Image Background",
      hasApplied: true,
      muted: false,
    };
  }
  if (
    type === "color" &&
    backgroundPreview?.color &&
    backgroundPreview.color !== "transparent"
  ) {
    return {
      kind: "color",
      label: "Color Background",
      hasApplied: true,
      muted: false,
    };
  }
  if (type === "ai") {
    return {
      kind: "ai",
      label: "AI Background",
      hasApplied: true,
      muted: false,
    };
  }
  return {
    kind: "transparent",
    label: "Transparent Background",
    hasApplied: false,
    muted: true,
  };
}

function shiftForIndex(index, fromIndex, toIndex, stride) {
  if (index === fromIndex) return 0;
  if (fromIndex < toIndex) {
    if (index > fromIndex && index <= toIndex) return -stride;
  } else if (fromIndex > toIndex) {
    if (index >= toIndex && index < fromIndex) return stride;
  }
  return 0;
}

const EditorLeftMenuLayersControls = ({
  onSelectLayer,
  onRemoveObject = null,
  objectPreviewUrl = null,
  backgroundPreview = null,
}) => {
  const layers = useEditorStore((s) => s.layers);
  const selectedLayerId = useEditorStore((s) => s.selectedLayerId);
  const toggleLayerVisibility = useEditorStore((s) => s.toggleLayerVisibility);
  const selectLayer = useEditorStore((s) => s.selectLayer);
  const reorderMovableLayers = useEditorStore((s) => s.reorderMovableLayers);
  const bringLayerForward = useEditorStore((s) => s.bringLayerForward);
  const sendLayerBackward = useEditorStore((s) => s.sendLayerBackward);
  const bringLayerToFront = useEditorStore((s) => s.bringLayerToFront);
  const sendLayerToBack = useEditorStore((s) => s.sendLayerToBack);
  const removeImageLayer = useEditorStore((s) => s.removeImageLayer);

  const listRef = useRef(null);
  const rowRefs = useRef({});
  const dragRef = useRef(null);
  const [drag, setDrag] = useState(null);

  const ordered = sortLayersTopFirst(layers);
  const movable = useMemo(
    () => ordered.filter((l) => l.id !== "background"),
    [ordered]
  );
  const backgroundLayer = ordered.find((l) => l.id === "background") || null;
  const bgDisplay = useMemo(
    () => getBackgroundDisplay(backgroundPreview),
    [backgroundPreview]
  );

  useEffect(() => {
    if (!drag) return undefined;

    const onMove = (e) => {
      const state = dragRef.current;
      if (!state) return;
      const y = e.clientY;
      const pointerY = y - state.grabOffsetY;
      let toIndex = state.fromIndex;
      for (let i = 0; i < state.centers.length; i += 1) {
        if (y < state.centers[i]) {
          toIndex = i;
          break;
        }
        toIndex = i;
      }
      toIndex = Math.max(0, Math.min(state.movableCount - 1, toIndex));
      setDrag((prev) =>
        prev
          ? {
              ...prev,
              pointerY,
              toIndex,
            }
          : prev
      );
      dragRef.current = {
        ...state,
        pointerY,
        toIndex,
      };
    };

    const onUp = () => {
      const state = dragRef.current;
      dragRef.current = null;
      setDrag(null);
      if (!state) return;
      const { id, fromIndex, toIndex, ids } = state;
      if (fromIndex === toIndex) return;
      const next = ids.filter((x) => x !== id);
      next.splice(toIndex, 0, id);
      reorderMovableLayers(next);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [drag, reorderMovableLayers]);

  const handleRowClick = (layer) => {
    if (dragRef.current) return;
    selectLayer(layer.id);
    onSelectLayer?.(layer.id);
  };

  const handleToggleVisibility = (e, layerId, disabled) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    toggleLayerVisibility(layerId);
  };

  const stopRowInteraction = (e) => {
    e.stopPropagation();
  };

  const handleDragHandlePointerDown = (e, layer) => {
    if (layer.id === "background") return;
    e.preventDefault();
    e.stopPropagation();

    const fromIndex = movable.findIndex((l) => l.id === layer.id);
    if (fromIndex < 0) return;

    const rowEl = rowRefs.current[layer.id];
    if (!rowEl) return;
    const rowRect = rowEl.getBoundingClientRect();
    const rowHeight = rowRect.height;
    const stride = rowHeight + LIST_GAP;
    const grabOffsetY = e.clientY - rowRect.top;

    const centers = movable.map((l) => {
      const el = rowRefs.current[l.id];
      if (!el) return 0;
      const r = el.getBoundingClientRect();
      return r.top + r.height / 2;
    });

    const next = {
      id: layer.id,
      fromIndex,
      toIndex: fromIndex,
      grabOffsetY,
      pointerY: e.clientY - grabOffsetY,
      rowHeight,
      stride,
      width: rowRect.width,
      left: rowRect.left,
      movableCount: movable.length,
      ids: movable.map((l) => l.id),
      centers,
    };
    dragRef.current = next;
    setDrag(next);
    // Highlight in store only — do NOT call onSelectLayer (that switches left-nav tabs).
    selectLayer(layer.id);
  };

  const renderThumb = (layer) => {
    if (layer.id === "object" && objectPreviewUrl) {
      return (
        <img
          className="layers-panel__thumb-img"
          src={objectPreviewUrl}
          alt=""
          draggable="false"
        />
      );
    }
    if (layer.type === "image" && layer.imageUrl) {
      return (
        <img
          className="layers-panel__thumb-img"
          src={layer.imageUrl}
          alt=""
          draggable="false"
        />
      );
    }
    if (layer.id === "background") {
      if (bgDisplay.kind === "image" && backgroundPreview?.imageUrl) {
        return (
          <img
            className="layers-panel__thumb-img"
            src={backgroundPreview.imageUrl}
            alt=""
            draggable="false"
          />
        );
      }
      const color =
        bgDisplay.kind === "color" && backgroundPreview?.color
          ? backgroundPreview.color
          : "transparent";
      return (
        <span
          className="layers-panel__thumb-swatch"
          style={{
            backgroundColor: color === "transparent" ? undefined : color,
          }}
          data-transparent={color === "transparent" ? "true" : "false"}
        />
      );
    }
    return <span className="layers-panel__thumb-swatch" data-transparent="true" />;
  };

  const layerLabel = (layer) => {
    if (layer.id === "background") return bgDisplay.label;
    return layer.label;
  };

  const renderRowContent = (
    layer,
    { canDrag, isBackground, visible, eyeDisabled = false, labelMuted = false }
  ) => {
    const label = layerLabel(layer);
    return (
      <>
        <span className="layers-panel__thumb">{renderThumb(layer)}</span>
        <span
          className={`layers-panel__label${
            labelMuted ? " layers-panel__label--muted" : ""
          }`}
        >
          {label}
        </span>
        <button
          type="button"
          className={`layers-panel__eye${
            eyeDisabled ? " layers-panel__eye--disabled" : ""
          }`}
          title={
            eyeDisabled
              ? "No background applied"
              : visible
                ? "Hide layer"
                : "Show layer"
          }
          aria-label={
            eyeDisabled
              ? "Hide background unavailable"
              : visible
                ? `Hide ${label}`
                : `Show ${label}`
          }
          aria-disabled={eyeDisabled}
          disabled={eyeDisabled}
          onMouseDown={stopRowInteraction}
          onClick={(e) => handleToggleVisibility(e, layer.id, eyeDisabled)}
        >
          <EyeIcon open={visible} />
        </button>
        {isBackground ? (
          <span
            className="layers-panel__lock"
            title="Background is locked"
            aria-label="Background is locked"
          >
            <LockIcon />
          </span>
        ) : layer.type === "image" || layer.id === "object" ? (
          <button
            type="button"
            className="layers-panel__delete"
            title="Delete layer"
            aria-label={`Delete ${label}`}
            onMouseDown={stopRowInteraction}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (layer.type === "image") {
                removeImageLayer(layer.id);
              } else if (layer.id === "object") {
                onRemoveObject?.();
              }
            }}
          >
            <TrashIcon />
          </button>
        ) : (
          <span className="layers-panel__action-spacer" aria-hidden="true" />
        )}
        <span
          className={`layers-panel__drag${
            canDrag ? "" : " layers-panel__drag--disabled"
          }`}
          title={canDrag ? "Drag to reorder" : "Background stays at bottom"}
          onPointerDown={
            canDrag ? (e) => handleDragHandlePointerDown(e, layer) : undefined
          }
          onClick={(e) => e.stopPropagation()}
        >
        <DragHandleIcon />
      </span>
      </>
    );
  };

  const selectedIsMovable =
    selectedLayerId && selectedLayerId !== "background";

  const draggingLayer = drag
    ? movable.find((l) => l.id === drag.id) || null
    : null;

  return (
    <div
      className="editor-left-menu-layers-controls-wrapper"
      style={{ "--layers-pad-x": `${PANEL_PAD_X}px` }}
    >
      <h1>Layers</h1>
      {selectedIsMovable && (
        <div className="layers-panel__position">
          <div className="layers-panel__position-btns">
            <button
              type="button"
              onClick={() => bringLayerToFront(selectedLayerId)}
            >
              <FrontIcon />
              <span>Front</span>
            </button>
            <button
              type="button"
              onClick={() => bringLayerForward(selectedLayerId)}
            >
              <ForwardIcon />
              <span>Forward</span>
            </button>
            <button
              type="button"
              onClick={() => sendLayerBackward(selectedLayerId)}
            >
              <BackwardIcon />
              <span>Backward</span>
            </button>
            <button
              type="button"
              onClick={() => sendLayerToBack(selectedLayerId)}
            >
              <BackIcon />
              <span>Back</span>
            </button>
          </div>
        </div>
      )}
      <ul
        ref={listRef}
        className={`layers-panel__list${
          drag ? " layers-panel__list--dragging" : ""
        }`}
        role="listbox"
        aria-label="Layers"
      >
        {movable.map((layer, index) => {
          const visible = isLayerVisible(ordered, layer.id);
          const selected = selectedLayerId === layer.id;
          const isDragging = drag?.id === layer.id;
          const shift =
            drag && !isDragging
              ? shiftForIndex(index, drag.fromIndex, drag.toIndex, drag.stride)
              : 0;

          return (
            <li
              key={layer.id}
              ref={(el) => {
                rowRefs.current[layer.id] = el;
              }}
              className="layers-panel__item"
              style={{
                transform: shift ? `translateY(${shift}px)` : undefined,
                zIndex: isDragging ? 2 : 1,
              }}
            >
              <div
                className={`layers-panel__row${
                  selected ? " layers-panel__row--selected" : ""
                }${!visible ? " layers-panel__row--hidden" : ""}${
                  isDragging ? " layers-panel__row--dragging" : ""
                }`}
                role="option"
                aria-selected={selected}
                tabIndex={0}
                onClick={() => handleRowClick(layer)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleRowClick(layer);
                  }
                }}
              >
                {renderRowContent(layer, {
                  canDrag: true,
                  isBackground: false,
                  visible,
                })}
              </div>
            </li>
          );
        })}
        {backgroundLayer ? (
          <li
            key={backgroundLayer.id}
            ref={(el) => {
              rowRefs.current[backgroundLayer.id] = el;
            }}
            className="layers-panel__item"
          >
            <div
              className={`layers-panel__row${
                selectedLayerId === backgroundLayer.id
                  ? " layers-panel__row--selected"
                  : ""
              }${
                !isLayerVisible(ordered, backgroundLayer.id)
                  ? " layers-panel__row--hidden"
                  : ""
              }${bgDisplay.muted ? " layers-panel__row--bg-empty" : ""}`}
              role="option"
              aria-selected={selectedLayerId === backgroundLayer.id}
              tabIndex={0}
              onClick={() => handleRowClick(backgroundLayer)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleRowClick(backgroundLayer);
                }
              }}
            >
              {renderRowContent(backgroundLayer, {
                canDrag: false,
                isBackground: true,
                visible: isLayerVisible(ordered, backgroundLayer.id),
                eyeDisabled: !bgDisplay.hasApplied,
                labelMuted: bgDisplay.muted,
              })}
            </div>
          </li>
        ) : null}
      </ul>

      {drag && draggingLayer ? (
        <div
          className="layers-panel__drag-ghost"
          style={{
            width: drag.width,
            left: drag.left,
            top: drag.pointerY,
            height: drag.rowHeight,
          }}
        >
          <div
            className={`layers-panel__row layers-panel__row--ghost${
              selectedLayerId === draggingLayer.id
                ? " layers-panel__row--selected"
                : ""
            }`}
          >
            {renderRowContent(draggingLayer, {
              canDrag: true,
              isBackground: false,
              visible: isLayerVisible(ordered, draggingLayer.id),
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default EditorLeftMenuLayersControls;
